import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import { initIntegrityGuard } from "./lib/integrityGuard";
import { installBootGuard, markBootSuccess } from "./lib/bootGuard";
import { initWebVitals } from "./lib/webVitals";
import { logBuildBanner, BUILD_VERSION } from "./lib/buildInfo";

// 🪪 Build banner — first thing in the console so you can confirm the bundle
logBuildBanner();
// Expose for ops / Sentry tag enrichment
// eslint-disable-next-line @typescript-eslint/no-explicit-any
(window as any).__BUILD_VERSION__ = BUILD_VERSION;

// 🛡️ Catch chunk-load / TDZ failures that crash BEFORE React mounts.
// Must run first — once a vendor chunk throws, no React boundary can help.
installBootGuard();

// 🛡️ Initialize Integrity Guard — anti-tampering & anti-cloning
initIntegrityGuard();

// Initialize Sentry lazily — don't block initial render
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
if (sentryDsn) {
  import("@sentry/react").then((Sentry) => {
    Sentry.init({
      dsn: sentryDsn,
      release: `cashbackbr@${BUILD_VERSION}`,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration(),
      ],
      tracesSampleRate: 0.3,
      replaysOnErrorSampleRate: 1.0,
      replaysSessionSampleRate: 0.05,
      environment: import.meta.env.MODE,
      initialScope: {
        tags: { build_version: BUILD_VERSION },
      },
      beforeSend(event) {
        if (event.user) {
          delete event.user.ip_address;
          delete event.user.email;
        }
        return event;
      },
    });
    // Expose globally so bootGuard / webVitals can report without an import cycle.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).Sentry = Sentry;
  });
}

createRoot(document.getElementById("root")!).render(<App />);

// Mark boot as successful so the bootGuard clears its one-shot reload flag.
markBootSuccess();

// 📊 Web Vitals — reports CLS/INP/LCP/FCP/TTFB to Sentry as measurements.
void initWebVitals();
